import { useQuery, useQueries, UseQueryResult } from '@tanstack/react-query';
import { clubApi } from '../services/apiService';
import { useTimeFrame } from '../contexts/TimeFrameContext';
import { TimePeriod } from '../types/api';
import { ClubMetrics, ClubSummaryData, ClubTrendDataItem } from '../types/club';
import { PERSIAN_MONTHS, MONTH_INDEX_MAP } from '../utils/dataHelpers';
import apiClient from '../utils/apiClient';
import { useMemo } from 'react';
import { logger } from '../utils/logger';

// Query keys for cache management
export const CLUB_QUERY_KEYS = {
  DASHBOARD: 'clubDashboard',
  BRANCH_DATA: 'clubBranchData',
  BRAND_DATA: 'clubBrandData',
  ALL_RESTAURANTS: 'clubAllRestaurants',
  HEATMAP: 'clubHeatmap',
  COMPARISON: 'clubComparison',
  TREND: 'clubTrend',
  SUMMARY: 'clubSummary',
  TWELVE_MONTH_AVERAGE: 'clubTwelveMonthAverage',
};

/**
 * Hook to fetch club dashboard data
 */
export function useClubDashboard(branchId: number | undefined, customTimeRange?: TimePeriod) {
  // Use the timeFrame from context
  const { apiTimeFrame } = useTimeFrame();
  
  // Use custom time range if provided, otherwise use the one from context
  const timeRange = customTimeRange || apiTimeFrame;
  
  return useQuery({
    queryKey: [CLUB_QUERY_KEYS.DASHBOARD, branchId, timeRange],
    queryFn: async () => {
      if (!branchId) throw new Error('Branch ID is required');
      
      try {
        const response = await clubApi.getDashboardData(branchId, timeRange);
        return response.data;
      } catch (error) {
        logger.error('Failed to fetch club dashboard data', 'ClubQueries', error);
        // Return empty data structure to prevent UI errors
        return {
          trends: [],
          kpis: {},
          rankData: {},
          membershipData: {},
          membershipTrend: []
        };
      }
    },
    enabled: !!branchId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
  });
}

/**
 * Hook to fetch club branch data for comparison
 */
export function useClubBranchData(branchId: number | undefined, customTimeRange?: TimePeriod) {
  // Use the timeFrame from context
  const { apiTimeFrame } = useTimeFrame();
  
  // Use custom time range if provided, otherwise use the one from context
  const timeRange = customTimeRange || apiTimeFrame;
  
  return useQuery({
    queryKey: [CLUB_QUERY_KEYS.BRANCH_DATA, branchId, timeRange],
    queryFn: async () => {
      if (!branchId) throw new Error('Branch ID is required');
      
      try {
        const response = await clubApi.getBranchData(branchId, timeRange);
        return response.data;
      } catch (error) {
        logger.error('Failed to fetch club branch data', 'ClubQueries', error);
        return [];
      }
    },
    enabled: !!branchId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to fetch club brand data
 */
export function useClubBrandData(brandId: number | undefined, customTimeRange?: TimePeriod) {
  // Use the timeFrame from context
  const { apiTimeFrame } = useTimeFrame();
  
  // Use custom time range if provided, otherwise use the one from context
  const timeRange = customTimeRange || apiTimeFrame;
  
  return useQuery({
    queryKey: [CLUB_QUERY_KEYS.BRAND_DATA, brandId, timeRange],
    queryFn: async () => {
      if (!brandId) throw new Error('Brand ID is required');
      
      try {
        const response = await clubApi.getBrandData(brandId, timeRange);
        return response.data;
      } catch (error) {
        logger.error('Failed to fetch club brand data', 'ClubQueries', error);
        return [];
      }
    },
    enabled: !!brandId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to fetch club all restaurants data
 */
export function useClubAllRestaurantsData(customTimeRange?: TimePeriod) {
  // Use the timeFrame from context
  const { apiTimeFrame } = useTimeFrame();
  
  // Use custom time range if provided, otherwise use the one from context
  const timeRange = customTimeRange || apiTimeFrame;
  
  return useQuery({
    queryKey: [CLUB_QUERY_KEYS.ALL_RESTAURANTS, timeRange],
    queryFn: async () => {
      try {
        const response = await clubApi.getAllRestaurantsData(timeRange);
        return response.data;
      } catch (error) {
        logger.error('Failed to fetch all restaurants data', 'ClubQueries', error);
        return [];
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to fetch all club comparison data in parallel
 * Used for the heatmap dashboard
 */
export function useClubComparisonData(
  branchId: number | undefined, 
  brandId: number | undefined,
  customTimeRange?: TimePeriod
) {
  // Use the timeFrame from context
  const { apiTimeFrame } = useTimeFrame();
  
  // Use custom time range if provided, otherwise use the one from context
  const timeRange = customTimeRange || apiTimeFrame;
  
  const results = useQueries({
    queries: [
      {
        queryKey: [CLUB_QUERY_KEYS.BRANCH_DATA, branchId, timeRange],
        queryFn: async () => {
          if (!branchId) throw new Error('Branch ID is required');
          try {
            const response = await clubApi.getBranchData(branchId, timeRange);
            return { type: 'branch', data: response.data };
          } catch (error) {
            logger.error('Failed to fetch club branch data', 'ClubQueries', error);
            return { type: 'branch', data: [] };
          }
        },
        enabled: !!branchId,
      },
      {
        queryKey: [CLUB_QUERY_KEYS.BRAND_DATA, brandId, timeRange],
        queryFn: async () => {
          if (!brandId) throw new Error('Brand ID is required');
          try {
            const response = await clubApi.getBrandData(brandId, timeRange);
            return { type: 'brand', data: response.data };
          } catch (error) {
            logger.error('Failed to fetch club brand data', 'ClubQueries', error);
            return { type: 'brand', data: [] };
          }
        },
        enabled: !!brandId,
      },
      {
        queryKey: [CLUB_QUERY_KEYS.ALL_RESTAURANTS, timeRange],
        queryFn: async () => {
          try {
            const response = await clubApi.getAllRestaurantsData(timeRange);
            return { type: 'allRestaurants', data: response.data };
          } catch (error) {
            logger.error('Failed to fetch all restaurants data', 'ClubQueries', error);
            return { type: 'allRestaurants', data: [] };
          }
        },
      }
    ]
  });

  // Extract results
  const [branchResult, brandResult, allRestaurantsResult] = results;

  return {
    branchData: branchResult.data?.data || [],
    brandData: brandResult.data?.data || [],
    allRestaurantsData: allRestaurantsResult.data?.data || [],
    isLoading: branchResult.isLoading || brandResult.isLoading || allRestaurantsResult.isLoading,
    isError: branchResult.isError || brandResult.isError || allRestaurantsResult.isError,
    error: branchResult.error || brandResult.error || allRestaurantsResult.error,
    refetch: () => {
      branchResult.refetch();
      brandResult.refetch();
      allRestaurantsResult.refetch();
    }
  };
}

interface HeatmapResponse {
  branchData: ClubMetrics[];
  brandData: ClubMetrics[];
  allRestaurantsData: ClubMetrics[];
}

interface TrendResponse {
  branchTrend: ClubTrendDataItem[];
  brandTrend: ClubTrendDataItem[];
  allRestaurantsTrend: ClubTrendDataItem[];
}

/**
 * Hook to fetch club heatmap data
 */
export function useClubHeatmapData(branchId: number | undefined, brandId: number | undefined, timeRange: string) {
  const queryResult = useQuery({
    queryKey: [CLUB_QUERY_KEYS.HEATMAP, branchId, timeRange],
    queryFn: async () => {
      if (!branchId) throw new Error('Branch ID is required');
      const months = timeRange === '1month' ? 1 : timeRange === '3months' ? 3 : timeRange === '6months' ? 6 : timeRange === '12months' ? 12 : 6;
      const response = await apiClient.get<{ success: boolean; data: HeatmapResponse }>(
        `/club-dashboard/${branchId}/heatmap?months=${months}`
      );
      return response.data.data;
    },
    enabled: !!branchId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Process data to create helper structures
  const processedData = useMemo(() => {
    const branchData = queryResult.data?.branchData || [];
    const brandData = queryResult.data?.brandData || [];
    const allRestaurantsData = queryResult.data?.allRestaurantsData || [];

    // Sort data by month number (descending - newest first)
    const sortedData = [...branchData].sort((a, b) => {
      return b.year - a.year || b.monthNum - a.monthNum;
    });

    // Determine how many months to show based on selected period
    const monthsToShow = timeRange === '1month' ? 1 : 
                        timeRange === '3months' ? 3 : 
                        timeRange === '6months' ? 6 : 
                        timeRange === '12months' ? 12 : 6;

    // Create a month list based on the selected period
    const allMonths: string[] = [];
    
    // Take only the number of months we need to show
    const dataToShow = sortedData.slice(-monthsToShow);
    
    dataToShow.forEach(item => {     
      allMonths.push(item.month);
    });

    // Map existing data to month
    const dataByMonth: { 
      byMonth: { [key: string]: { data: ClubMetrics, index: number } },
      byMonthName: { [key: string]: { data: ClubMetrics, index: number } } 
    } = {
      byMonth: {},
      byMonthName: {}
    };
    
    sortedData.forEach((item, index) => {
      const monthKey = item.month;
      dataByMonth.byMonth[monthKey] = { data: item, index };
      
      // Also map by month name without year prefix for flexible matching
      const monthNameOnly = monthKey;
      dataByMonth.byMonthName[monthNameOnly] = { data: item, index };
    });

    return {
      branchData: sortedData,
      brandData,
      allRestaurantsData,
      allMonths,
      dataByMonth
    };
  }, [queryResult.data, timeRange]);

  return {
    ...processedData,
    isLoading: queryResult.isLoading,
    isError: queryResult.isError,
    error: queryResult.error,
    refetch: queryResult.refetch
  };
}

/**
 * Hook to fetch club trend data
 */
export function useClubTrendData(branchId: number | undefined, brandId: number | undefined, metric: string, timeRange?: string) {
  const { apiTimeFrame } = useTimeFrame();
  const effectiveTimeRange = timeRange || apiTimeFrame;
  
  const months = effectiveTimeRange === '1month' ? 1 : 
                effectiveTimeRange === '3months' ? 3 : 
                effectiveTimeRange === '6months' ? 6 : 
                effectiveTimeRange === '12months' ? 12 : 6;

  return useQuery({
    queryKey: [CLUB_QUERY_KEYS.TREND, branchId, metric, months],
    queryFn: async () => {
      if (!branchId) throw new Error('Branch ID is required');
      const response = await apiClient.get<{ success: boolean; data: TrendResponse }>(
        `/club-dashboard/${branchId}/trend?metric=${metric}&months=${months}`
      );
      return response.data.data;
    },
    enabled: !!branchId && !!metric,
    staleTime: 5 * 60 * 1000, // 5 minutes
    select: (data) => ({
      trendData: data.branchTrend || [],
      brandTrendData: data.brandTrend || [],
      allRestaurantsTrendData: data.allRestaurantsTrend || []
    })
  });
}

/**
 * Hook to fetch club summary data
 */
export function useClubSummaryData(branchId: number | undefined, brandId: number | undefined, timeRange?: string) {
  const { apiTimeFrame } = useTimeFrame();
  const effectiveTimeRange = timeRange || apiTimeFrame;
  
  const months = effectiveTimeRange === '1month' ? 1 : 
                effectiveTimeRange === '3months' ? 3 : 
                effectiveTimeRange === '6months' ? 6 : 
                effectiveTimeRange === '12months' ? 12 : 6;

  return useQuery({
    queryKey: [CLUB_QUERY_KEYS.SUMMARY, branchId, months],
    queryFn: async () => {
      if (!branchId) throw new Error('Branch ID is required');
      const response = await apiClient.get<{ success: boolean; data: ClubSummaryData }>(
        `/club-dashboard/${branchId}/summary?months=${months}`
      );
      return response.data.data;
    },
    enabled: !!branchId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    select: (data) => ({ summaryData: data })
  });
}

/**
 * Hook to fetch 12-month average data for club metrics
 */
export function useClub12MonthAverage(branchId: number | undefined) {
  return useQuery({
    queryKey: [CLUB_QUERY_KEYS.TWELVE_MONTH_AVERAGE, branchId],
    queryFn: async () => {
      if (!branchId) throw new Error('Branch ID is required');
      
      const response = await apiClient.get<{ 
        success: boolean; 
        data: { 
          averages: Record<string, number>;
          monthCount: number;
        } 
      }>(
        `/club-dashboard/${branchId}/metrics/12month-average`
      );
      return response.data.data;
    },
    enabled: !!branchId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}